
import { useState } from 'react';
import { Product } from '../page';
import { useAuth } from '../../../contexts/AuthContext';

interface ProductDetailProps {
  product: Product;
  onEdit: () => void;
  onBack: () => void;
  onUpdateVariantQuantity: (productId: string, size: string, color: string, quantity: number) => void;
}

export default function ProductDetail({ product, onEdit, onBack, onUpdateVariantQuantity }: ProductDetailProps) {
  const { userProfile } = useAuth();
  const [editingVariant, setEditingVariant] = useState<{ size: string; color: string } | null>(null);
  const [newQuantity, setNewQuantity] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string>(product.image);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Check if user is partner
  const isPartner = userProfile?.role === 'partner';
  const isManager = userProfile?.role === 'manager' || userProfile?.role === 'admin';

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const getTotalQuantity = () => {
    return product.variants.reduce((total, variant) => total + variant.quantity, 0);
  };

  const getStockStatus = (quantity: number) => {
    if (quantity === 0) return { text: 'Hết hàng', color: 'text-red-600 bg-red-100' };
    if (quantity <= 5) return { text: 'Sắp hết', color: 'text-orange-600 bg-orange-100' };
    return { text: 'Còn hàng', color: 'text-green-600 bg-green-100' };
  };

  const handleQuantityUpdate = (size: string, color: string) => {
    if (newQuantity >= 0) {
      onUpdateVariantQuantity(product.id, size, color, newQuantity);
      setEditingVariant(null);
    }
  };

  const startEditing = (size: string, color: string, currentQuantity: number) => {
    setEditingVariant({ size, color });
    setNewQuantity(currentQuantity);
  };

  // Calculate discounted price for partners
  const getDiscountedPrice = () => {
    const commissionRate = userProfile?.commission_rate || 15;
    const discountAmount = (product.sellingPrice * commissionRate) / 100;
    return product.sellingPrice - discountAmount;
  };

  // Get all available images (main product images + variant images)
  const getAllImages = () => {
    const allImages = [...(product.images || [product.image])];
    
    product.variants.forEach(variant => {
      if (variant.images) {
        variant.images.forEach(img => {
          if (!allImages.includes(img)) {
            allImages.push(img);
          }
        });
      }
    });
    
    return allImages.filter(img => img && img.trim() !== '');
  };

  const allImages = getAllImages();

  const nextImage = () => {
    if (allImages.length > 1) {
      const nextIndex = (currentImageIndex + 1) % allImages.length;
      setCurrentImageIndex(nextIndex);
      setSelectedImage(allImages[nextIndex]);
    }
  };

  const prevImage = () => {
    if (allImages.length > 1) {
      const prevIndex = currentImageIndex === 0 ? allImages.length - 1 : currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      setSelectedImage(allImages[prevIndex]);
    }
  };

  const selectImage = (imageUrl: string) => {
    const index = allImages.indexOf(imageUrl);
    if (index !== -1) {
      setCurrentImageIndex(index);
      setSelectedImage(imageUrl);
    }
  };

  const totalQuantity = getTotalQuantity();
  const stockStatus = getStockStatus(totalQuantity);
  const discountedPrice = getDiscountedPrice();
  const profit = isPartner ? product.sellingPrice - discountedPrice : product.sellingPrice - product.productionPrice;
  const profitMargin = isPartner 
    ? ((product.sellingPrice - discountedPrice) / discountedPrice * 100).toFixed(1)
    : ((profit / product.productionPrice) * 100).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-800"
          >
            <i className="ri-arrow-left-line text-2xl"></i>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chi tiết sản phẩm</h1>
            <p className="text-gray-600">Thông tin chi tiết và quản lý sản phẩm</p>
          </div>
        </div>
        {/* Only show edit button for managers */}
        {isManager && (
          <button
            onClick={onEdit}
            className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center space-x-2 whitespace-nowrap"
          >
            <i className="ri-edit-line"></i>
            <span>Chỉnh sửa</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Images and Basic Info */}
        <div className="space-y-6">
          {/* Product Images Gallery */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Ảnh sản phẩm ({allImages.length})
            </h3>
            
            {/* Main Image Display */}
            <div className="relative aspect-square mb-4">
              <img
                src={selectedImage}
                alt={product.name}
                className="w-full h-full object-cover object-center rounded-lg"
              />
              <div className="absolute top-4 right-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${stockStatus.color}`}>
                  {stockStatus.text}
                </span>
              </div>
              
              {/* Navigation arrows */}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                  >
                    <i className="ri-arrow-left-line"></i>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                  >
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  
                  {/* Image counter */}
                  <div className="absolute bottom-4 left-4 bg-black/50 text-white px-2 py-1 rounded text-sm">
                    {currentImageIndex + 1} / {allImages.length}
                  </div>
                </>
              )}
            </div>
            
            {/* Thumbnail Gallery */}
            {allImages.length > 1 && (
              <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                {allImages.map((imageUrl, index) => (
                  <button
                    key={index}
                    onClick={() => selectImage(imageUrl)}
                    className={`aspect-square border-2 rounded-lg overflow-hidden ${
                      selectedImage === imageUrl ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={imageUrl}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover object-center"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin cơ bản</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Mã sản phẩm:</span>
                <span className="font-medium text-gray-900">{product.id}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tên sản phẩm:</span>
                <span className="font-medium text-gray-900">{product.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Danh mục:</span>
                <span className="font-medium text-gray-900">{product.category}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Ngày tạo:</span>
                <span className="font-medium text-gray-900">
                  {new Date(product.createdAt).toLocaleDateString('vi-VN')}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Cập nhật cuối:</span>
                <span className="font-medium text-gray-900">
                  {new Date(product.updatedAt).toLocaleDateString('vi-VN')}
                </span>
              </div>
            </div>
            
            {product.description && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Mô tả:</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
              </div>
            )}
          </div>

          {/* Pricing Information - Different for Partners vs Managers */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin giá</h3>
            <div className="space-y-4">
              {isPartner ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Giá sản phẩm đã triết khấu hoa hồng:</span>
                    <span className="font-medium text-gray-900">{formatPrice(discountedPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Giá bán:</span>
                    <span className="font-semibold text-green-600">{formatPrice(product.sellingPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-gray-600">Lợi nhuận/sản phẩm:</span>
                    <span className="font-semibold text-blue-600">{formatPrice(profit)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Tỷ suất lợi nhuận:</span>
                    <span className="font-semibold text-blue-600">{profitMargin}%</span>
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <i className="ri-information-line mr-1"></i>
                      Hoa hồng của bạn: {userProfile?.commission_rate || 15}%
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Giá sản xuất:</span>
                    <span className="font-medium text-gray-900">{formatPrice(product.productionPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Giá bán:</span>
                    <span className="font-semibold text-green-600">{formatPrice(product.sellingPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-gray-600">Lợi nhuận/sản phẩm:</span>
                    <span className="font-semibold text-blue-600">{formatPrice(profit)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Tỷ suất lợi nhuận:</span>
                    <span className="font-semibold text-blue-600">{profitMargin}%</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Inventory Details */}
        <div className="space-y-6">
          {/* Total Inventory Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tổng quan kho hàng</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tổng số lượng:</span>
                <span className="text-2xl font-bold text-gray-900">{totalQuantity} cái</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Trạng thái:</span>
                <span className={`px-2 py-1 rounded-full text-sm font-medium ${stockStatus.color}`}>
                  {stockStatus.text}
                </span>
              </div>
              
              {/* Only show inventory values for managers */}
              {isManager && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Giá trị tồn kho:</span>
                    <span className="font-semibold text-gray-900">
                      {formatPrice(totalQuantity * product.productionPrice)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Giá trị bán lẻ:</span>
                    <span className="font-semibold text-green-600">
                      {formatPrice(totalQuantity * product.sellingPrice)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Detailed Inventory by Variants with Images */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Chi tiết kho theo Size & Màu</h3>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {product.variants.map((variant) => {
                const variantStockStatus = getStockStatus(variant.quantity);
                const isEditing = editingVariant?.size === variant.size && editingVariant?.color === variant.color;
                
                return (
                  <div key={`${variant.size}-${variant.color}`} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start space-x-3">
                        {variant.images && variant.images.length > 0 && (
                          <div className="flex-shrink-0">
                            <div className="flex space-x-1">
                              {variant.images.slice(0, 2).map((imageUrl, index) => (
                                <button
                                  key={index}
                                  onClick={() => selectImage(imageUrl)}
                                  className="w-12 h-12 border-2 border-gray-200 hover:border-blue-400 rounded overflow-hidden transition-colors"
                                >
                                  <img
                                    src={imageUrl}
                                    alt={`${variant.size} - ${variant.color} ${index + 1}`}
                                    className="w-full h-full object-cover object-center"
                                  />
                                </button>
                              ))}
                              {variant.images.length > 2 && (
                                <div className="w-12 h-12 border-2 border-gray-200 rounded flex items-center justify-center bg-gray-50 text-xs text-gray-500">
                                  +{variant.images.length - 2}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {variant.size} - {variant.color}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${variantStockStatus.color} w-fit mt-1`}>
                            {variantStockStatus.text}
                          </span>
                          {variant.images && variant.images.length > 0 && (
                            <span className="text-xs text-gray-500 mt-1">
                              {variant.images.length} ảnh
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        {/* Only allow editing for managers */}
                        {isManager && isEditing ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={newQuantity}
                              onChange={(e) => setNewQuantity(parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                              min="0"
                            />
                            <button
                              onClick={() => handleQuantityUpdate(variant.size, variant.color)}
                              className="text-green-600 hover:text-green-700 p-1"
                            >
                              <i className="ri-check-line"></i>
                            </button>
                            <button
                              onClick={() => setEditingVariant(null)}
                              className="text-red-600 hover:text-red-700 p-1"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-lg font-bold text-gray-900 min-w-[3rem] text-right">
                              {variant.quantity}
                            </span>
                            <span className="text-sm text-gray-500">cái</span>
                            {/* Only show edit button for managers */}
                            {isManager && (
                              <button
                                onClick={() => startEditing(variant.size, variant.color, variant.quantity)}
                                className="text-blue-600 hover:text-blue-700 p-1"
                              >
                                <i className="ri-edit-line text-sm"></i>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Available Sizes & Colors */}
          <div className="grid grid-cols-1 gap-6">
            {/* Sizes */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Size/Phiên bản có sẵn</h3>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map(size => {
                  const sizeQuantity = product.variants
                    .filter(v => v.size === size)
                    .reduce((total, v) => total + v.quantity, 0);
                  
                  return (
                    <div key={size} className="flex flex-col items-center">
                      <span className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium">
                        {size}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">{sizeQuantity} cái</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Colors */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Màu sắc có sẵn</h3>
              <div className="flex flex-wrap gap-2">
                {product.colors.map(color => {
                  const colorQuantity = product.variants
                    .filter(v => v.color === color)
                    .reduce((total, v) => total + v.quantity, 0);
                  
                  return (
                    <div key={color} className="flex flex-col items-center">
                      <span className="px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                        {color}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">{colorQuantity} cái</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
